'use client'

import { FC, SetStateAction, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { usePathname } from 'next/navigation'
import {
  ChatbotUIContext,
  type AttachmentScopedState,
  type ScopedAttachmentBindings
} from '@/context/context'
import { QuickActionsProvider } from '@/context/quick-actions'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { getPopularAssistants } from '@/db/assistants'
import { getPlatformTools } from '@/db/platform-tools'
import { getProfileByUserId } from '@/db/profile'
import { getAssistantPublicImageUrl } from '@/db/storage/assistant-images'
import { getWorkspaceImageFromStorage } from '@/db/storage/workspace-images'
import { getCustomIntegrationToolsByUserId, getPublicTools } from '@/db/tools'
import { getWorkspaceById, getWorkspacesByUserId } from '@/db/workspaces'
import { Tables } from '@/supabase/types'
import {
  Application,
  ChatActivityStatus,
  ChatFile,
  ChatMessage,
  ChatSettings,
  LLM,
  LLMID,
  MessageImage,
  ModelProvider,
  OpenRouterLLM,
  WorkspaceImage
} from '@/types'
import { useRouter } from 'nextjs-toploader/app'

import { AssistantImage } from '@/types/images/assistant-image'
import type { SelectedSpaceItem, SpaceMemberWithProfile, SpaceNote } from '@/types/spaces'
import { VALID_ENV_KEYS } from '@/types/valid-keys'
import { convertBlobToBase64 } from '@/lib/blob-to-b64'
import { isMobileScreen } from '@/lib/mobile'
import { getDesktopRuntimeConfig } from '@/lib/desktop-runtime'
import { getRetrievalSourceLimit } from '@/lib/retrieval/source-limits'
import {
  fetchHostedModels,
  fetchOllamaModels,
  fetchOpenRouterModels
} from '@/lib/models/fetch-models'
import { LLM_LIST, resolveProviderForModel } from '@/lib/models/llm/llm-list'
import { supabase } from '@/lib/supabase/browser-client'
import { buildChatShellCacheKey, mergeChatShellLists } from '@/lib/chat/chat-shell'
import { clearAllSidebarChatSnapshots } from '@/lib/sidebar/sidebar-chat-store'
import { safeConsole } from '@/lib/utils/safeConsole'
import { onlyUniqueById } from '@/lib/utils'
import {
  readSelectedWorkspaceIdFromStorage,
  clearSelectedWorkspaceIdFromStorage,
  persistSelectedWorkspaceIdToStorage
} from '@/lib/workspaces/selected-workspace-storage'
import { resolvePreferredWorkspace as resolvePreferredWorkspaceForUser } from '@/lib/workspaces/resolve-preferred-workspace'
import {
  clearSelectedSpaceContextFromStorage,
  persistSelectedSpaceContextToStorage,
  readSelectedSpaceContextFromStorage
} from '@/lib/spaces/selected-space-context-storage'
import { WorkspaceLoadingShell } from '@/components/utility/workspace-loading-shell'
import { CreatePrompt } from '@/components/sidebar/items/prompts/create-prompt'
import { useRootMountMarker } from '@/components/utility/root-mount-marker'
import { toast } from 'sonner'
import { useTimezoneSetup } from '@/lib/hooks/useTimezoneSetup'
import { useServerEvents } from '@/hooks/useServerEvents'
import { useAuth } from '@/context/auth'
import { isDesktopHost } from '@/lib/desktop-host'
import {
  CHAT_ACTIVITY_REGISTRY_EVENT,
  CHAT_ACTIVITY_REGISTRY_STORAGE_KEY,
  markChatActivitySeen as markChatActivitySeenInRegistry,
  readChatActivityRegistry
} from '@/lib/chat/client-activity-registry'
import { buildSeedRunFromAnnotation } from '@/lib/sidecar-seed'
import {
  createSidecarChatStatus,
  getLatestSidecarChatStatuses,
  markSidecarChatSeen as markSidecarChatSeenInRegistry,
  readSidecarRunRegistry,
  SIDECAR_CHAT_REGISTRY_STORAGE_KEY,
  SIDECAR_REGISTRY_EVENT,
  upsertSidecarChatStatus,
  writeSidecarChatRegistry
} from '@/lib/sidecar/client-registry'
import { listenInvitationAccepted } from '@/lib/invitations/post-accept-sync'
import type { SidecarChatStatus, SidecarRunEventData } from '@/types/sidecar'

interface GlobalStateProps {
  children: React.ReactNode
}

const WORKSPACE_PULSE_WARMUP_DELAY_MS = 2_500
const MODELS_IDLE_TIMEOUT_MS = 4_000
const MODELS_FALLBACK_DELAY_MS = 1_500
const SPACE_ROUTE_MODELS_DELAY_MS = 12_000
const DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS = 8_000
// Web bootstrap (profile + workspaces) previously had NO timeout:
// a hung Supabase query or a half-open fetch left `loading` true
// forever behind the skeleton. Bound each attempt so the existing
// retry path (and the shell's manual Retry) can actually fire.
const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 15_000
const PLATFORM_TOOLS_RECOVERY_COOLDOWN_MS = 5_000

const shouldRedirectToLoginOnSignOut = (pathname: string | null) => {
  if (!pathname) return false

  return (
    pathname.startsWith('/chat') ||
    pathname.startsWith('/spaces') ||
    pathname.startsWith('/items') ||
    pathname.startsWith('/nexus') ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/setup') ||
    pathname.startsWith('/studio') ||
    pathname.startsWith('/agent') ||
    pathname.startsWith('/a/')
  )
}

const scheduleModelsLoadForRoute = (pathname: string | null, callback: () => void) => {
  if (typeof window === 'undefined') {
    return
  }

  if (isDesktopHost()) {
    callback()
    return
  }

  if (pathname?.startsWith('/spaces')) {
    window.setTimeout(callback, SPACE_ROUTE_MODELS_DELAY_MS)
    return
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(
      () => {
        callback()
      },
      { timeout: MODELS_IDLE_TIMEOUT_MS }
    )
    return
  }

  window.setTimeout(callback, MODELS_FALLBACK_DELAY_MS)
}

const isDesktopBrowserRuntime = () => typeof window !== 'undefined' && isDesktopHost()

const normalizeToolForContext = (tool: Tables<'tools'>) => ({
  ...tool,
  parameters: () => tool.schema || {},
  can_be_used_in_applications: false
})

const mergeToolCatalogs = (...tools: Tables<'tools'>[]) =>
  tools.filter(onlyUniqueById).map(normalizeToolForContext)

const haveSameToolIds = (left: Tables<'tools'>[], right: Tables<'tools'>[]) =>
  left.length === right.length && left.every((tool, index) => tool.id === right[index]?.id)

const MODEL_PROVIDERS = new Set<ModelProvider>([
  'marshmallow',
  'openai',
  'google',
  'anthropic',
  'mistral',
  'groq',
  'perplexity',
  'ollama',
  'openrouter',
  'custom',
  'databricks',
  'meta',
  'cohere',
  'microsoft',
  'deepseek'
])

function normalizeDesktopRuntimeProvider(
  provider: string | undefined,
  modelId: string
): ModelProvider {
  const normalizedProvider = provider?.trim().toLowerCase()

  if (normalizedProvider === 'azure') {
    return 'openai'
  }

  if (normalizedProvider && MODEL_PROVIDERS.has(normalizedProvider as ModelProvider)) {
    return normalizedProvider as ModelProvider
  }

  return resolveProviderForModel(modelId) || 'custom'
}

function normalizeDesktopRuntimeTier(tier: string | undefined): LLM['tier'] {
  if (tier === 'free' || tier === 'plus' || tier === 'pro' || tier === 'ultra') {
    return tier
  }

  if (tier === 'ultimate') {
    return 'ultra'
  }

  return undefined
}

function mapDesktopRuntimeModelToLlm(option: DesktopRuntimeModelOption): LLM {
  const knownModel = LLM_LIST.find(
    (model) => model.modelId === option.id || model.hostedId === option.id
  )
  const provider = normalizeDesktopRuntimeProvider(option.ownedBy || option.owned_by, option.id)
  const runtimeDefaultTools = option.defaultToolIds || option.default_tool_ids
  const defaultTools =
    Array.isArray(runtimeDefaultTools) && runtimeDefaultTools.length > 0
      ? runtimeDefaultTools
      : knownModel?.defaultTools
  const runtimeToolsSupported =
    typeof option.tools === 'boolean'
      ? option.tools
      : typeof option.toolsSupported === 'boolean'
        ? option.toolsSupported
        : undefined
  const toolsSupported =
    knownModel?.tools === true ? true : (runtimeToolsSupported ?? knownModel?.tools)
  const imageInput =
    typeof option.imageInput === 'boolean'
      ? option.imageInput
      : typeof option.image_input === 'boolean'
        ? option.image_input
        : knownModel?.imageInput
  const runtimeSupportsStreaming =
    typeof option.supportsStreaming === 'boolean'
      ? option.supportsStreaming
      : typeof option.supports_streaming === 'boolean'
        ? option.supports_streaming
        : undefined
  const supportsStreaming =
    knownModel?.supportsStreaming === true
      ? true
      : (runtimeSupportsStreaming ?? knownModel?.supportsStreaming)

  return {
    ...(knownModel || {}),
    modelId: option.id as LLMID,
    modelName: option.label || knownModel?.modelName || option.id,
    provider,
    hostedId: knownModel?.hostedId || option.id,
    platformLink: knownModel?.platformLink || '',
    imageInput: imageInput ?? false,
    tier: normalizeDesktopRuntimeTier(option.tier) || knownModel?.tier,
    tools: toolsSupported ?? false,
    supportsStreaming,
    badge: option.badge || knownModel?.badge,
    defaultTools
  }
}

function getDesktopRuntimeModels(snapshot: DesktopRuntimeSnapshot | null): LLM[] {
  if (!snapshot) {
    return []
  }

  const optionsById = new Map<string, DesktopRuntimeModelOption>()

  snapshot.modelOptions.forEach((option) => {
    if (option.id) {
      optionsById.set(option.id, option)
    }
  })

  snapshot.models.forEach((modelId) => {
    if (!optionsById.has(modelId)) {
      optionsById.set(modelId, {
        id: modelId,
        label: LLM_LIST.find((model) => model.modelId === modelId)?.modelName || modelId
      })
    }
  })

  return Array.from(optionsById.values()).map(mapDesktopRuntimeModelToLlm)
}

function mapDesktopRuntimeToolToRow(tool: DesktopRuntimeTool): Tables<'tools'> {
  const now = new Date(0).toISOString()

  return {
    id: tool.id,
    name: tool.name,
    description: tool.description || '',
    sharing: tool.sharing || 'platform',
    schema: {},
    custom_headers: {},
    url: '',
    user_id: '',
    folder_id: null,
    project_id: null,
    can_be_used_in_applications: false,
    created_at: now,
    updated_at: now
  } as Tables<'tools'>
}

function getDesktopRuntimeTools(snapshot: DesktopRuntimeSnapshot | null): Tables<'tools'>[] {
  return snapshot?.tools.map(mapDesktopRuntimeToolToRow) || []
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  if (typeof window === 'undefined') {
    return promise
  }

  let timeoutId: number | null = null
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}

async function fetchDesktopProfileByUserId(userId: string) {
  const response = await withTimeout(
    fetch('/api/v1/profile', {
      cache: 'no-store',
      credentials: 'include'
    }),
    DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
    'Desktop profile bootstrap'
  ).catch((error) => {
    safeConsole.warn('Desktop profile API bootstrap failed:', error)
    return null
  })

  if (!response?.ok) {
    return null
  }

  const profile = await response.json().catch(() => null)

  if (!profile || profile.user_id !== userId) {
    return null
  }

  return profile as Tables<'profiles'>
}

async function fetchDesktopWorkspacesByUserId(userId: string) {
  const response = await withTimeout(
    fetch('/api/v1/workspaces', {
      cache: 'no-store',
      credentials: 'include'
    }),
    DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
    'Desktop workspace bootstrap'
  ).catch((error) => {
    safeConsole.warn('Desktop workspaces API bootstrap failed:', error)
    return null
  })

  if (!response?.ok) {
    return null
  }

  const payload = await response.json().catch(() => null)
  const workspaces = payload?.workspaces

  if (!Array.isArray(workspaces)) {
    return null
  }

  return workspaces as Tables<'workspaces'>[]
}

async function fetchDesktopWorkspaceContext(workspaceId: string) {
  const response = await withTimeout(
    fetch(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/context`, {
      cache: 'no-store',
      credentials: 'include'
    }),
    DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
    'Desktop workspace context bootstrap'
  ).catch((error) => {
    safeConsole.warn('Desktop workspace context API bootstrap failed:', error)
    return null
  })

  if (!response?.ok) {
    return null
  }

  const payload = await response.json().catch(() => null)
  const workspace = payload?.workspace

  if (!workspace || workspace.id !== workspaceId) {
    return null
  }

  return workspace as WorkspaceWithRelations
}

async function getInitialProfileByUserId(userId: string, authProfile?: Tables<'profiles'> | null) {
  if (authProfile?.user_id === userId) {
    return authProfile
  }

  if (isDesktopBrowserRuntime()) {
    const desktopProfile = await fetchDesktopProfileByUserId(userId)
    if (desktopProfile) {
      return desktopProfile
    }
  }

  return getProfileByUserId(userId)
}

async function getInitialWorkspacesByUserId(userId: string) {
  if (isDesktopBrowserRuntime()) {
    const desktopWorkspaces = await fetchDesktopWorkspacesByUserId(userId)
    if (desktopWorkspaces) {
      return desktopWorkspaces
    }
  }

  return getWorkspacesByUserId(userId)
}

type WorkspaceWithRelations = Tables<'workspaces'> & {
  folders: Tables<'folders'>[] | null
  files: Tables<'files'>[] | null
  presets: Tables<'presets'>[] | null
  prompts: Tables<'prompts'>[] | null
  tools: Tables<'tools'>[] | null
  models: Tables<'models'>[] | null
}

type WorkspaceQueryBuilder = {
  select: (columns: string) => {
    eq: (
      column: string,
      value: string
    ) => {
      single: () => Promise<{
        data: WorkspaceWithRelations | null
        error: { message: string } | null
      }>
    }
  }
}

function createEmptyAttachmentScopedState(): AttachmentScopedState {
  return {
    chatFiles: [],
    chatImages: [],
    newMessageFiles: [],
    newMessageImages: [],
    showFilesDisplay: false
  }
}

function resolveStateAction<Value>(value: SetStateAction<Value>, previous: Value): Value {
  return typeof value === 'function' ? (value as (previous: Value) => Value)(previous) : value
}

export const GlobalState: FC<GlobalStateProps> = ({ children }) => {
  const router = useRouter()
  const pathname = usePathname()
  const { session: authSession, user: authUser, profile: authProfile } = useAuth()
  const pathnameRef = useRef<string | null>(pathname)
  const routerRef = useRef(router)

  useRootMountMarker('global-state')

  // Reference to track initialization status
  const initRef = useRef({
    initialized: false,
    profileLoaded: false,
    workspacesLoaded: false,
    workspaceDataLoaded: false,
    chatsLoaded: false,
    modelsLoaded: false,
    initialFetchAttempted: false,
    retryCount: 0,
    lastRecoveryAttemptAt: 0,
    lastChatRecoveryAttemptAt: 0,
    platformToolsLoaded: false,
    platformToolsRecoveryCount: 0,
    lastPlatformToolsRecoveryAttemptAt: 0
  })

  // PROFILE STORE
  const [profile, setProfile] = useState<Tables<'profiles'> | null>(null)

  // ITEMS STORE
  const [assistants, setAssistants] = useState<Tables<'assistants'>[]>([])
  const [collections, setCollections] = useState<Tables<'collections'>[]>([])
  const [chats, setChats] = useState<Tables<'chats'>[]>([])
  const [hasMoreChats, setHasMoreChats] = useState(true)
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false)
  const [chatsCursor, setChatsCursor] = useState<string | null>(null)
  const [files, setFiles] = useState<Tables<'files'>[]>([])
  const [folders, setFolders] = useState<Tables<'folders'>[]>([])
  const [models, setModels] = useState<Tables<'models'>[]>([])
  const [presets, setPresets] = useState<Tables<'presets'>[]>([])
  const [prompts, setPrompts] = useState<Tables<'prompts'>[]>([])
  const [tools, setTools] = useState<Tables<'tools'>[]>([])
  const [platformTools, setPlatformTools] = useState<Tables<'tools'>[]>([])
  const [workspaces, setWorkspaces] = useState<Tables<'workspaces'>[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [initializationAttempt, setInitializationAttempt] = useState(0)

  // MODELS STORE
  const [envKeyMap, setEnvKeyMap] = useState<Record<string, VALID_ENV_KEYS>>({})
  const [availableHostedModels, setAvailableHostedModels] = useState<LLM[]>([])
  const [availableLocalModels, setAvailableLocalModels] = useState<LLM[]>([])
  const [availableOpenRouterModels, setAvailableOpenRouterModels] = useState<OpenRouterLLM[]>([])
  const [allModels, setAllModels] = useState<LLM[]>([])

  // WORKSPACE STORE
  const [selectedWorkspace, setSelectedWorkspaceState] = useState<Tables<'workspaces'> | null>(null)
  const [workspaceImages, setWorkspaceImages] = useState<WorkspaceImage[]>([])

  // PRESET STORE
  const [selectedPreset, setSelectedPreset] = useState<Tables<'presets'> | null>(null)

  // ASSISTANT STORE
  const [selectedAssistant, setSelectedAssistant] = useState<Tables<'assistants'> | null>(null)
  const [assistantImages, setAssistantImages] = useState<AssistantImage[]>([])
  const [openaiAssistants, setOpenaiAssistants] = useState<any[]>([])

  const [applications, setApplications] = useState<Application[]>([])
  const [spaceNotes, setSpaceNotes] = useState<SpaceNote[]>([])
  const [spaces, setSpaces] = useState<Tables<'spaces'>[]>([])
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false)
  const [selectedSpace, setSelectedSpace] = useState<Tables<'spaces'> | null>(null)
  const [spaceMembers, setSpaceMembers] = useState<SpaceMemberWithProfile[]>([])
  const [selectedSpaceItem, setSelectedSpaceItem] = useState<SelectedSpaceItem>(null)
  const [chatActivityStatuses, setChatActivityStatuses] = useState<
    Record<string, ChatActivityStatus>
  >({})
  const [sidecarRunStatuses, setSidecarRunStatuses] = useState<Record<string, SidecarChatStatus>>(
    {}
  )
  const sidecarChatStatuses = useMemo(
    () => getLatestSidecarChatStatuses(sidecarRunStatuses),
    [sidecarRunStatuses]
  )
  const setSidecarChatStatuses = useCallback(
    (value: SetStateAction<Record<string, SidecarChatStatus>>) => {
      setSidecarRunStatuses((previous) => {
        const nextStatuses = resolveStateAction(value, getLatestSidecarChatStatuses(previous))

        return Object.fromEntries(
          Object.values(nextStatuses).map((status) => [`${status.chatId}:${status.runId}`, status])
        )
      })
    },
    []
  )
  const loadedWorkspaceRef = useRef<string | null>(null)
  const loadingWorkspaceRef = useRef<string | null>(null)
  const pulseWarmupWorkspaceIdsRef = useRef<Set<string>>(new Set())
  const selectedWorkspaceIdRef = useRef<string | null>(null)
  const workspaceSelectionVersionRef = useRef(0)
  const currentSessionUserIdRef = useRef<string | null>(null)
  const latestAuthUserIdRef = useRef<string | null>(null)
  const authProviderSyncedUserIdRef = useRef<string | null>(null)
  const scheduledModelsLoadUserIdRef = useRef<string | null>(null)
  const platformToolsRecoveryInFlightRef = useRef(false)
  const [hydratedSpaceContextScopeKey, setHydratedSpaceContextScopeKey] = useState<string | null>(
    null
  )

  const getSpaceContextScopeKey = useCallback(
    (userId: string | null | undefined, workspaceId: string | null | undefined) => {
      if (!userId || !workspaceId) {
        return null
      }

      return `${userId}:${workspaceId}`
    },
    []
  )

  const applyPlatformTools = useCallback((nextPlatformTools: Tables<'tools'>[]) => {
    if (nextPlatformTools.length === 0) {
      return
    }

    initRef.current.platformToolsLoaded = true
    setPlatformTools(nextPlatformTools)
    setTools((currentTools) => {
      const mergedTools = mergeToolCatalogs(...nextPlatformTools, ...currentTools)

      return haveSameToolIds(mergedTools, currentTools) ? currentTools : mergedTools
    })
  }, [])

  const recoverPlatformTools = useCallback(async (reason: string) => {
    const now = Date.now()

    if (
      platformToolsRecoveryInFlightRef.current ||
      initRef.current.platformToolsRecoveryCount >= 3 ||
      now - initRef.current.lastPlatformToolsRecoveryAttemptAt <
        PLATFORM_TOOLS_RECOVERY_COOLDOWN_MS
    ) {
      return
    }

    platformToolsRecoveryInFlightRef.current = true
    initRef.current.platformToolsRecoveryCount += 1
    initRef.current.lastPlatformToolsRecoveryAttemptAt = now

    try {
      const nextPlatformTools = await getPlatformTools({
        isAuthenticated: Boolean(currentSessionUserIdRef.current),
        forceRefresh: true
      })

      if (nextPlatformTools.length === 0) {
        safeConsole.warn(`Platform tools recovery returned no tools: ${reason}`)
        return
      }

      applyPlatformTools(nextPlatformTools)
    } catch (error) {
      safeConsole.warn(`Platform tools recovery failed: ${reason}`, error)
    } finally {
      platformToolsRecoveryInFlightRef.current = false
    }
  }, [applyPlatformTools])

  // Manual recovery from the loading shell (>10s state). Resets the
  // bootstrap guards so initializeApp runs again; the shell remounts
  // via key={initializationAttempt} so its slow timer restarts.
  const handleBootstrapRetry = useCallback(() => {
    initRef.current.retryCount = 0
    initRef.current.lastRecoveryAttemptAt = 0
    initRef.current.initialFetchAttempted = false
    initRef.current.initialized = false
    setLoading(true)
    setInitializationAttempt((current) => current + 1)
  }, [])

  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  useEffect(() => {
    latestAuthUserIdRef.current = authSession?.user?.id || authUser?.id || null
  }, [authSession?.user?.id, authUser?.id])

  useEffect(() => {
    routerRef.current = router
  }, [router])

  const resetWorkspaceScopedState = (options?: { clearLoadedWorkspace?: boolean }) => {
    setChats([])
    setChatsCursor(null)
    setHasMoreChats(true)
    setIsLoadingMoreChats(false)
    setFolders([])
    setFiles([])
    setPrompts([])
    setAssistants([])
    setModels([])
    setChatMessages([])
    setSelectedChat(null)
    setSpaces([])
    setSpaceNotes([])
    setSpaceMembers([])
    setSelectedSpace(null)
    setSelectedSpaceItem(null)
    initRef.current.workspaceDataLoaded = false

    if (options?.clearLoadedWorkspace !== false) {
      loadedWorkspaceRef.current = null
      loadingWorkspaceRef.current = null
    }
  }

  const clearWorkspaceClientStorage = (userId?: string | null) => {
    if (typeof window === 'undefined') return

    try {
      const targetUserId = userId || currentSessionUserIdRef.current

      if (targetUserId) {
        clearSelectedWorkspaceIdFromStorage(targetUserId)
        clearSelectedSpaceContextFromStorage({
          userId: targetUserId
        })
      }

      localStorage.removeItem('selectedFolderId')

      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('chats_') || key.startsWith('chat_shell_'))
        .forEach((key) => sessionStorage.removeItem(key))

      clearAllSidebarChatSnapshots()
    } catch {
      // Ignore storage cleanup issues during sign out.
    }
  }

  const prewarmWorkspacePulse = useCallback((workspaceId: string) => {
    if (typeof window === 'undefined') {
      return
    }

    if (pulseWarmupWorkspaceIdsRef.current.has(workspaceId)) {
      return
    }

    pulseWarmupWorkspaceIdsRef.current.add(workspaceId)

    const startWarmup = () => {
      void authenticatedFetch('/api/spaces/pulse-warmup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workspaceId
        })
      }).catch((error) => {
        pulseWarmupWorkspaceIdsRef.current.delete(workspaceId)
        console.error('Failed to prewarm workspace Pulse:', error)
      })
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          startWarmup()
        },
        {
          timeout: WORKSPACE_PULSE_WARMUP_DELAY_MS
        }
      )
      return
    }

    window.setTimeout(() => {
      startWarmup()
    }, WORKSPACE_PULSE_WARMUP_DELAY_MS)
  }, [])

  const resolveWorkspaceSelection = useCallback(
    (
      availableWorkspaces: Tables<'workspaces'>[],
      currentUserId: string | null | undefined,
      preferredWorkspaceId?: string | null
    ) =>
      resolvePreferredWorkspaceForUser({
        availableWorkspaces,
        currentUserId,
        preferredWorkspaceId
      }),
    []
  )

  const selectWorkspace = useCallback((nextWorkspace: Tables<'workspaces'> | null) => {
    const nextWorkspaceId = nextWorkspace?.id || null
    const previousWorkspaceId = selectedWorkspaceIdRef.current

    if (previousWorkspaceId !== nextWorkspaceId) {
      workspaceSelectionVersionRef.current += 1
      loadedWorkspaceRef.current = null
    }

    selectedWorkspaceIdRef.current = nextWorkspaceId
    persistSelectedWorkspaceIdToStorage(nextWorkspaceId, currentSessionUserIdRef.current)
    setSelectedWorkspaceState(nextWorkspace)

    return workspaceSelectionVersionRef.current
  }, [])

  const setSelectedWorkspace = useCallback(
    (value: SetStateAction<Tables<'workspaces'> | null>) => {
      if (typeof value !== 'function') {
        selectWorkspace(value)
        return
      }

      setSelectedWorkspaceState((previousWorkspace) => {
        const nextWorkspace = resolveStateAction(value, previousWorkspace)
        const nextWorkspaceId = nextWorkspace?.id || null
        const previousWorkspaceId = selectedWorkspaceIdRef.current || null

        if (previousWorkspaceId !== nextWorkspaceId) {
          workspaceSelectionVersionRef.current += 1
          loadedWorkspaceRef.current = null
        }

        selectedWorkspaceIdRef.current = nextWorkspaceId
        persistSelectedWorkspaceIdToStorage(nextWorkspaceId, currentSessionUserIdRef.current)

        return nextWorkspace
      })
    },
    [selectWorkspace]
  )

  const isWorkspaceSelectionCurrent = useCallback(
    (workspaceId: string, _selectionVersion?: number | null) => {
      if (selectedWorkspaceIdRef.current !== workspaceId) {
        return false
      }

      return true
    },
    []
  )

  const hydrateSessionWorkspaceState = async (userId: string) => {
    currentSessionUserIdRef.current = userId
    authProviderSyncedUserIdRef.current = userId

    const [profileData, workspacesData] = await withTimeout(
      Promise.all([
        getProfileByUserId(userId),
        getWorkspacesByUserId(userId)
      ]),
      WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
      'Workspace session hydration'
    )

    setProfile(profileData)
    setWorkspaces(workspacesData)

    if (!profileData || workspacesData.length === 0) {
      selectWorkspace(null)
      resetWorkspaceScopedState()
      return
    }

    const preferredWorkspaceId = readSelectedWorkspaceIdFromStorage(userId)
    const nextWorkspace = resolveWorkspaceSelection(
      workspacesData,
      profileData.user_id as string | null | undefined,
      preferredWorkspaceId
    )
    if (!nextWorkspace) {
      selectWorkspace(null)
      resetWorkspaceScopedState()
      return
    }

    const selectionVersion = selectWorkspace(nextWorkspace)
    setWorkspaceImages([])
    scheduleWorkspaceImagesLoad(workspacesData)

    setChatSettings((prevSettings) => ({
      model: 'losi-omega' as LLMID,
      prompt: nextWorkspace.default_prompt || 'You are a friendly, helpful AI assistant.',
      temperature: nextWorkspace.default_temperature || 0.5,
      contextLength: nextWorkspace.default_context_length || 4096,
      includeProfileContext: nextWorkspace.include_profile_context || true,
      includeWorkspaceInstructions: nextWorkspace.include_workspace_instructions || true,
      embeddingsProvider: (nextWorkspace.embeddings_provider as 'openai' | 'local') || 'openai',
      reflectMode: prevSettings?.reflectMode || false
    }))

    await Promise.all([
      loadChatData(nextWorkspace.id, userId, selectionVersion),
      loadWorkspaceData(nextWorkspace.id, userId, true, selectionVersion)
    ])

    if (profileData?.user_id && scheduledModelsLoadUserIdRef.current !== profileData.user_id) {
      const scheduledUserId = profileData.user_id
      scheduledModelsLoadUserIdRef.current = scheduledUserId

      scheduleModelsLoadForRoute(pathnameRef.current, () => {
        if (currentSessionUserIdRef.current !== scheduledUserId) {
          if (scheduledModelsLoadUserIdRef.current === scheduledUserId) {
            scheduledModelsLoadUserIdRef.current = null
          }
          return
        }

        void loadModels(profileData).catch((error) => {
          if (scheduledModelsLoadUserIdRef.current === scheduledUserId) {
            scheduledModelsLoadUserIdRef.current = null
          }
          safeConsole.error('Error loading models after session hydration:', error)
        })
      })
    }

    prewarmWorkspacePulse(nextWorkspace.id)
  }

  const refreshAccessibleWorkspaces = useCallback(
    async (targetWorkspaceId?: string | null) => {
      const profileUserId = profile?.user_id as string | undefined

      if (!profileUserId) {
        return []
      }

      try {
        const nextWorkspaces = await getWorkspacesByUserId(profileUserId)
        setWorkspaces(nextWorkspaces)

        if (nextWorkspaces.length === 0) {
          selectWorkspace(null)
          resetWorkspaceScopedState()
          return []
        }

        const preferredWorkspaceId =
          targetWorkspaceId ||
          selectedWorkspaceIdRef.current ||
          selectedWorkspace?.id ||
          readSelectedWorkspaceIdFromStorage(profileUserId) ||
          null
        const nextSelectedWorkspace = resolveWorkspaceSelection(
          nextWorkspaces,
          profileUserId,
          preferredWorkspaceId
        )
        if (!nextSelectedWorkspace) {
          selectWorkspace(null)
          resetWorkspaceScopedState()
          return nextWorkspaces
        }

        const previousWorkspaceId = selectedWorkspaceIdRef.current || null
        const selectionVersion = selectWorkspace(nextSelectedWorkspace)

        const shouldReloadWorkspaceData =
          nextSelectedWorkspace.id !== previousWorkspaceId ||
          !initRef.current.workspaceDataLoaded ||
          loadedWorkspaceRef.current !== nextSelectedWorkspace.id

        if (shouldReloadWorkspaceData) {
          await Promise.all([
            loadChatData(nextSelectedWorkspace.id, profileUserId, selectionVersion, {
              forceFresh: true
            }),
            loadWorkspaceData(nextSelectedWorkspace.id, profileUserId, true, selectionVersion)
          ])
          prewarmWorkspacePulse(nextSelectedWorkspace.id)
        } else {
          await loadChatData(nextSelectedWorkspace.id, profileUserId, selectionVersion, {
            forceFresh: true
          })
        }

        return nextWorkspaces
      } catch (error) {
        safeConsole.error('Failed to refresh accessible workspaces:', error)
        return []
      }
    },
    [
      prewarmWorkspacePulse,
      profile?.user_id,
      resolveWorkspaceSelection,
      selectWorkspace,
      selectedWorkspace?.id
    ]
  )

  useEffect(() => {
    return listenInvitationAccepted((detail) => {
      void refreshAccessibleWorkspaces(
        detail.preserveSelectedWorkspace ? undefined : detail.workspaceId
      )
    })
  }, [refreshAccessibleWorkspaces])

  // PASSIVE CHAT STORE
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatSettings, setChatSettings] = useState<ChatSettings>(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedSettings = window?.localStorage?.getItem('chatSettings')

        if (storedSettings) {
          return JSON.parse(storedSettings)
        }
      } catch (error) {
        console.error('Error getting chat settings:', error)
      }
    }

    return {
      model: 'losi-omega',
      prompt: 'You are a helpful AI assistant.',
      temperature: 0.5,
      contextLength: 4000,
      includeProfileContext: true,
      includeWorkspaceInstructions: true,
      embeddingsProvider: 'openai',
      reflectMode: false
    }
  })
  const [selectedChat, setSelectedChat] = useState<Tables<'chats'> | null>(null)
  const routeChatId = pathname?.startsWith('/chat/')
    ? pathname.split('/').filter(Boolean)[1] || null
    : pathname === '/chat'
      ? null
      : undefined
  const activeChatId = pathname?.startsWith('/chat/') ? routeChatId : null
  const [chatFileItems, setChatFileItems] = useState<Tables<'file_items'>[]>([])

  useEffect(() => {
    if (!selectedChat?.id) {
      return
    }

    const refreshedSelectedChat = chats.find((chat) => chat.id === selectedChat.id)

    if (!refreshedSelectedChat) {
      return
    }

    if (JSON.stringify(refreshedSelectedChat) === JSON.stringify(selectedChat)) {
      return
    }

    setSelectedChat(refreshedSelectedChat)
  }, [chats, selectedChat])

  // ACTIVE CHAT STORE
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const [firstTokenReceived, setFirstTokenReceived] = useState<boolean>(false)
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  // CHAT INPUT COMMAND STORE
  const [isMessageHistoryPickerOpen, setIsMessageHistoryPickerOpen] = useState(false)
  const [isPromptPickerOpen, setIsPromptPickerOpen] = useState(false)
  const [slashCommand, setSlashCommand] = useState('')
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false)
  const [hashtagCommand, setHashtagCommand] = useState('')
  const [isToolPickerOpen, setIsToolPickerOpen] = useState(false)
  const [toolCommand, setToolCommand] = useState('')
  const [focusPrompt, setFocusPrompt] = useState(false)
  const [focusFile, setFocusFile] = useState(false)
  const [focusTool, setFocusTool] = useState(false)
  const [focusAssistant, setFocusAssistant] = useState(false)
  const [atCommand, setAtCommand] = useState('')
  const [isAssistantPickerOpen, setIsAssistantPickerOpen] = useState(false)
  const [showPromptVariables, setShowPromptVariables] = useState(false)
  const [promptVariables, setPromptVariables] = useState<
    {
      promptId: string
      name: string
      value: string
    }[]
  >([])

  // ATTACHMENTS STORE
  const getAttachmentSurfacePrefix = useCallback(() => {
    if (pathname?.startsWith('/chat')) return 'chat'
    if (pathname?.startsWith('/try')) return 'public'
    if (pathname?.startsWith('/nexus')) return 'nexus'
    if (pathname?.startsWith('/spaces')) return 'spaces'
    if (pathname?.startsWith('/agent')) return 'agent'
    if (pathname?.startsWith('/apps')) return 'apps'
    if (pathname?.startsWith('/applications')) return 'applications'
    return 'global'
  }, [pathname])

  const getAttachmentStoreKey = useCallback(
    (chatId?: string | null) => {
      const trimmedChatId = typeof chatId === 'string' ? chatId.trim() : ''
      const prefix = getAttachmentSurfacePrefix()
      return trimmedChatId ? `${prefix}:${trimmedChatId}` : `${prefix}:draft`
    },
    [getAttachmentSurfacePrefix]
  )
  const [activeAttachmentOverrideId, setActiveAttachmentOverrideId] = useState<string | null>(null)

  const activeAttachmentKey = pathname?.startsWith('/chat')
    ? getAttachmentStoreKey(routeChatId)
    : getAttachmentStoreKey(activeAttachmentOverrideId || selectedChat?.id)

  const [attachmentStateByKey, setAttachmentStateByKey] = useState<
    Record<string, AttachmentScopedState>
  >({})
  const attachmentStateRef = useRef<Record<string, AttachmentScopedState>>(attachmentStateByKey)
  const commitAttachmentStateByKey = useCallback(
    (nextStateByKey: Record<string, AttachmentScopedState>) => {
      attachmentStateRef.current = nextStateByKey
      return nextStateByKey
    },
    []
  )

  useEffect(() => {
    attachmentStateRef.current = attachmentStateByKey
  }, [attachmentStateByKey])

  useEffect(() => {
    setAttachmentStateByKey((previous) => {
      if (previous[activeAttachmentKey]) {
        return previous
      }

      return commitAttachmentStateByKey({
        ...previous,
        [activeAttachmentKey]: createEmptyAttachmentScopedState()
      })
    })
  }, [activeAttachmentKey, commitAttachmentStateByKey])

  const getAttachmentStateSnapshot = useCallback((attachmentKey: string): AttachmentScopedState => {
    return attachmentStateRef.current[attachmentKey] ?? createEmptyAttachmentScopedState()
  }, [])

  const updateScopedAttachmentState = useCallback(
    <Key extends keyof AttachmentScopedState>(
      attachmentKey: string,
      key: Key,
      value: SetStateAction<AttachmentScopedState[Key]>
    ) => {
      setAttachmentStateByKey((previous) => {
        const current = previous[attachmentKey] ?? createEmptyAttachmentScopedState()
        const nextValue = resolveStateAction(value, current[key])

        if (Object.is(current[key], nextValue)) {
          return previous
        }

        return commitAttachmentStateByKey({
          ...previous,
          [attachmentKey]: {
            ...current,
            [key]: nextValue
          }
        })
      })
    },
    [commitAttachmentStateByKey]
  )

  const getScopedAttachmentBindings = useCallback(
    (attachmentKey: string): ScopedAttachmentBindings => ({
      getSnapshot: () => getAttachmentStateSnapshot(attachmentKey),
      setChatFiles: (value) => updateScopedAttachmentState(attachmentKey, 'chatFiles', value),
      setChatImages: (value) => updateScopedAttachmentState(attachmentKey, 'chatImages', value),
      setNewMessageFiles: (value) =>
        updateScopedAttachmentState(attachmentKey, 'newMessageFiles', value),
      setNewMessageImages: (value) =>
        updateScopedAttachmentState(attachmentKey, 'newMessageImages', value),
      setShowFilesDisplay: (value) =>
        updateScopedAttachmentState(attachmentKey, 'showFilesDisplay', value)
    }),
    [getAttachmentStateSnapshot, updateScopedAttachmentState]
  )

  const migrateAttachmentState = useCallback(
    (fromAttachmentKey: string, toAttachmentKey: string) => {
      if (!fromAttachmentKey || !toAttachmentKey || fromAttachmentKey === toAttachmentKey) {
        return
      }

      setAttachmentStateByKey((previous) => {
        const fromState = previous[fromAttachmentKey]
        if (!fromState) return previous

        const toState = previous[toAttachmentKey] ?? createEmptyAttachmentScopedState()
        const nextState: AttachmentScopedState = {
          chatFiles: fromState.chatFiles.length > 0 ? fromState.chatFiles : toState.chatFiles,
          chatImages: fromState.chatImages.length > 0 ? fromState.chatImages : toState.chatImages,
          newMessageFiles:
            fromState.newMessageFiles.length > 0
              ? fromState.newMessageFiles
              : toState.newMessageFiles,
          newMessageImages:
            fromState.newMessageImages.length > 0
              ? fromState.newMessageImages
              : toState.newMessageImages,
          showFilesDisplay: fromState.showFilesDisplay || toState.showFilesDisplay
        }

        const updatedState = {
          ...previous,
          [toAttachmentKey]: nextState
        }

        delete updatedState[fromAttachmentKey]

        return commitAttachmentStateByKey(updatedState)
      })
    },
    [commitAttachmentStateByKey]
  )

  const activeAttachmentState = getAttachmentStateSnapshot(activeAttachmentKey)
  const chatFiles = activeAttachmentState.chatFiles
  const chatImages = activeAttachmentState.chatImages
  const newMessageFiles = activeAttachmentState.newMessageFiles
  const newMessageImages = activeAttachmentState.newMessageImages
  const showFilesDisplay = activeAttachmentState.showFilesDisplay
  const setChatFiles = getScopedAttachmentBindings(activeAttachmentKey).setChatFiles
  const setChatImages = getScopedAttachmentBindings(activeAttachmentKey).setChatImages
  const setNewMessageFiles = getScopedAttachmentBindings(activeAttachmentKey).setNewMessageFiles
  const setNewMessageImages = getScopedAttachmentBindings(activeAttachmentKey).setNewMessageImages
  const setShowFilesDisplay = getScopedAttachmentBindings(activeAttachmentKey).setShowFilesDisplay

  // RETIEVAL STORE
  const [useRetrieval, setUseRetrieval] = useState<boolean>(true)
  const sourceCountTouchedRef = useRef(false)
  const [sourceCount, setSourceCountState] = useState<number>(() =>
    getRetrievalSourceLimit(null)
  )
  const setSourceCount = useCallback((value: SetStateAction<number>) => {
    sourceCountTouchedRef.current = true
    setSourceCountState(value)
  }, [])

  useEffect(() => {
    if (sourceCountTouchedRef.current) return
    setSourceCountState(getRetrievalSourceLimit(profile))
  }, [profile?.nexus_plan, profile?.nexus_plan_status, profile?.plan])

  // TOOL STORE
  const [selectedTools, setSelectedTools] = useState<Tables<'tools'>[]>([])
  const [toolInUse, setToolInUse] = useState<string>('none')

  // PAYWALL
  const [isPaywallOpen, setIsPaywallOpen] = useState<boolean>(false)

  // PROFILE SETTINGS
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState<string>('')

  // SIDEBAR
  const [showSidebar, setShowSidebar] = useState<boolean>(() => !isMobileScreen())

  // CREATE PROMPT (opened from prompt picker or sidebar)
  const [isCreatePromptOpen, setIsCreatePromptOpen] = useState<boolean>(false)

  // THINK DEEPER
  const [isThinkDeeperEnabled, setIsThinkDeeperEnabled] = useState<boolean>(false)

  // Use the timezone setup hook at component level
  useTimezoneSetup(selectedWorkspace?.id || '')
  const { addEventListener, removeEventListener } = useServerEvents()

  useEffect(() => {
    const handleChatRenamed = (event: {
      data?: {
        chat?: Tables<'chats'>
        chatId?: string | null
      }
    }) => {
      const chat = event.data?.chat

      if (!chat?.id) {
        return
      }

      setChats((currentChats) =>
        currentChats.map((currentChat) =>
          currentChat.id === chat.id
            ? { ...currentChat, ...chat, updated_at: currentChat.updated_at }
            : currentChat
        )
      )
      setSelectedChat((currentChat) =>
        currentChat?.id === chat.id
          ? { ...currentChat, ...chat, updated_at: currentChat.updated_at }
          : currentChat
      )
    }

    addEventListener('chat.renamed', handleChatRenamed)

    return () => {
      removeEventListener('chat.renamed', handleChatRenamed)
    }
  }, [addEventListener, removeEventListener])

  // Primary initialization useEffect
  useEffect(() => {
    if (initRef.current.initialized || initRef.current.initialFetchAttempted) return

    const initializeApp = async () => {
      initRef.current.initialFetchAttempted = true
      try {
        // Check authentication first
        const session =
          authSession?.user || authUser
            ? ({
                ...(authSession || {}),
                user: authSession?.user || authUser
              } as NonNullable<typeof authSession>)
            : null

        if (!session?.user) {
          currentSessionUserIdRef.current = null
          scheduledModelsLoadUserIdRef.current = null
          pulseWarmupWorkspaceIdsRef.current.clear()
          setHydratedSpaceContextScopeKey(null)
          setLoading(false)
          // Allow public routes without authentication
          if (pathname?.startsWith('/chat/') && pathname !== '/try') {
            router.push('/login')
          }
          // For public routes, load platform tools
          if (pathname === '/try') {
            try {
              const platformToolsData = await getPlatformTools({ isAuthenticated: false })
              if (platformToolsData && platformToolsData.length > 0) {
                applyPlatformTools(platformToolsData)
              }
            } catch (error) {
              console.error('Failed to load platform tools for public user:', error)
            }
            return
          }
          return
        }

        currentSessionUserIdRef.current = session.user.id

        // CRITICAL PRIORITY: Load profile and workspaces - these are absolutely needed
        const [profile, workspacesData] = await withTimeout(
          Promise.all([
            getInitialProfileByUserId(session.user.id, authProfile),
            getInitialWorkspacesByUserId(session.user.id)
          ]),
          WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
          'Workspace bootstrap'
        )

        // Update initialization tracking
        initRef.current.profileLoaded = !!profile
        initRef.current.workspacesLoaded = workspacesData.length > 0

        // Set profile data
        setProfile(profile)

        if (workspacesData.length > 0) {
          setWorkspaces(workspacesData)
          const preferredWorkspaceId = readSelectedWorkspaceIdFromStorage(session.user.id)
          const nextSelectedWorkspace = resolveWorkspaceSelection(
            workspacesData,
            session.user.id,
            preferredWorkspaceId
          )

          if (!nextSelectedWorkspace) {
            setLoading(false)
            initRef.current.retryCount = 0
            initRef.current.lastRecoveryAttemptAt = 0
            initRef.current.initialized = true
            return
          }

          const selectionVersion = selectWorkspace(nextSelectedWorkspace)

          // Initialize chat settings
          setChatSettings((prevSettings) => ({
            model: 'losi-omega' as LLMID,
            prompt:
              nextSelectedWorkspace.default_prompt || 'You are a friendly, helpful AI assistant.',
            temperature: nextSelectedWorkspace.default_temperature || 0.5,
            contextLength: nextSelectedWorkspace.default_context_length || 4096,
            includeProfileContext: nextSelectedWorkspace.include_profile_context || true,
            includeWorkspaceInstructions:
              nextSelectedWorkspace.include_workspace_instructions || true,
            embeddingsProvider:
              (nextSelectedWorkspace.embeddings_provider as 'openai' | 'local') || 'openai',
            reflectMode: prevSettings?.reflectMode || false
          }))

          // Start chat shell loading immediately, but do not block the
          // entire private app on chat preload.
          const workspaceId = nextSelectedWorkspace.id
          const userId = session.user.id

          prewarmWorkspacePulse(workspaceId)

          void loadChatData(workspaceId, userId, selectionVersion)

          // App is now usable once the workspace shell is selected.
          setLoading(false)

          // MEDIUM PRIORITY: Load workspace data and models in parallel
          void loadWorkspaceData(workspaceId, userId, true, selectionVersion).catch((error) => {
            console.error('Error loading workspace data:', error)
          })

          if (profile?.user_id && scheduledModelsLoadUserIdRef.current !== profile.user_id) {
            const scheduledUserId = profile.user_id
            scheduledModelsLoadUserIdRef.current = scheduledUserId

            scheduleModelsLoadForRoute(pathnameRef.current, () => {
              if (currentSessionUserIdRef.current !== scheduledUserId) {
                if (scheduledModelsLoadUserIdRef.current === scheduledUserId) {
                  scheduledModelsLoadUserIdRef.current = null
                }
                return
              }

              void loadModels(profile).catch((error) => {
                if (scheduledModelsLoadUserIdRef.current === scheduledUserId) {
                  scheduledModelsLoadUserIdRef.current = null
                }
                console.error('Error loading models:', error)
              })
            })
          }

          // LOW PRIORITY: Load images in background
          scheduleWorkspaceImagesLoad(workspacesData)

          initRef.current.retryCount = 0
          initRef.current.lastRecoveryAttemptAt = 0
          initRef.current.initialized = true
        } else {
          // No workspaces, app can still be usable
          setLoading(false)
          initRef.current.retryCount = 0
          initRef.current.lastRecoveryAttemptAt = 0
          initRef.current.initialized = true
        }
      } catch (error) {
        console.error('Error initializing application:', error)

        // Handle retry logic
        const maxRetries = 3
        if (initRef.current.retryCount < maxRetries) {
          initRef.current.retryCount++
          safeConsole.log(
            `Retrying initialization (${initRef.current.retryCount}/${maxRetries})...`
          )

          // Exponential backoff for retries
          setTimeout(
            () => {
              initRef.current.initialFetchAttempted = false
              initRef.current.initialized = false
              setInitializationAttempt((current) => current + 1)
            },
            1000 * Math.pow(2, initRef.current.retryCount - 1)
          )
        } else {
          // If we've exhausted retries, show an error and stop loading
          toast.error('Failed to initialize application. Please refresh the page.')
          setLoading(false)
        }
      }
    }

    initializeApp()
  }, [
    applyPlatformTools,
    authProfile,
    authSession,
    authUser,
    initializationAttempt,
    pathname,
    router
  ])

  // Chat data loading function - this should be quick and focused
  const loadChatData = async (
    workspaceId: string,
    userId: string,
    selectionVersion?: number | null,
    options?: {
      forceFresh?: boolean
    }
  ) => {
    try {
      // Check cache first for instant loading
      const cacheKey = buildChatShellCacheKey(workspaceId, userId)
      const cachedChats = options?.forceFresh ? null : sessionStorage.getItem(cacheKey)

      if (cachedChats) {
        try {
          const parsed = JSON.parse(cachedChats)
          if (parsed.timestamp && Date.now() - parsed.timestamp < 30000) {
            // 30 second cache
            if (!isWorkspaceSelectionCurrent(workspaceId, selectionVersion)) {
              return
            }

            setChats(parsed.chats || [])
            setHasMoreChats(parsed.hasMore !== false)
            setChatsCursor(parsed.nextCursor || null)
            initRef.current.chatsLoaded = true

            // Load fresh data in background
            loadFreshChatData(workspaceId, userId, cacheKey, selectionVersion)
            return
          }
        } catch (cacheError) {
          safeConsole.warn('Cache parse error:', cacheError)
        }
      }

      // Load fresh data
      await loadFreshChatData(workspaceId, userId, cacheKey, selectionVersion)
    } catch (error) {
      console.error('Error loading chat data:', error)
    }
  }

  const loadFreshChatData = async (
    workspaceId: string,
    userId: string,
    cacheKey: string,
    selectionVersion?: number | null
  ) => {
    try {
      // Import the paginated function
      const { getChatsByWorkspaceIdPaginated } = await import('@/db/chats')
      const { getUserGroupChats } = await import('@/db/chat-members')

      const [workspaceChatsResult, groupChatsResult] = await Promise.allSettled([
        getChatsByWorkspaceIdPaginated(workspaceId, 25),
        getUserGroupChats(userId, workspaceId)
      ])

      if (workspaceChatsResult.status !== 'fulfilled') {
        throw workspaceChatsResult.reason
      }

      if (groupChatsResult.status === 'rejected') {
        safeConsole.warn(
          'Failed to load accessible group chats for workspace shell:',
          groupChatsResult.reason
        )
      }

      const result = workspaceChatsResult.value
      const groupChats = groupChatsResult.status === 'fulfilled' ? groupChatsResult.value : []
      const { filterTombstonedChats, pruneTombstonesAgainstServerChats } =
        await import('@/lib/sidebar/deleted-chat-tombstones')
      const serverMerged = mergeChatShellLists(result.chats, groupChats)
      // Prune only when the server list confirms absence — pass unfiltered ids.
      pruneTombstonesAgainstServerChats(serverMerged.map(c => c.id))
      const mergedChats = filterTombstonedChats(serverMerged)

      if (!isWorkspaceSelectionCurrent(workspaceId, selectionVersion)) {
        return
      }

      setChats(mergedChats)
      setHasMoreChats(result.hasMore)
      setChatsCursor(result.nextCursor)
      initRef.current.chatsLoaded = true

      // Cache the result
      const cacheData = {
        chats: mergedChats,
        hasMore: result.hasMore,
        nextCursor: result.nextCursor,
        timestamp: Date.now()
      }

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))
      } catch (cacheError) {
        safeConsole.warn('Failed to cache chat data:', cacheError)
      }
    } catch (error) {
      console.error('Error loading fresh chat data:', error)
    }
  }

  // Function to load more chats
  const loadMoreChats = async () => {
    if (!selectedWorkspace || isLoadingMoreChats || !hasMoreChats || !chatsCursor) {
      return
    }

    setIsLoadingMoreChats(true)
    try {
      const { getChatsByWorkspaceIdPaginated } = await import('@/db/chats')

      const result = await getChatsByWorkspaceIdPaginated(
        selectedWorkspace.id,
        25, // Load smaller batches for better performance
        chatsCursor
      )

      setChats((prev) => {
        const mergedChats = mergeChatShellLists(prev, result.chats)

        // If we have too many chats in memory, remove oldest ones to prevent memory issues
        if (mergedChats.length > 1000) {
          safeConsole.log('Trimming chat list to prevent memory issues')
          return mergedChats.slice(0, 800)
        }
        return mergedChats
      })
      setHasMoreChats(result.hasMore)
      setChatsCursor(result.nextCursor)
    } catch (error) {
      console.error('Error loading more chats:', error)
    } finally {
      setIsLoadingMoreChats(false)
    }
  }

  // Model loading function
  const loadModels = async (profile: Tables<'profiles'> | null) => {
    try {
      const [desktopRuntimeConfig, hostedModelRes] = await Promise.all([
        isDesktopBrowserRuntime()
          ? getDesktopRuntimeConfig({
              backgroundRefresh: true,
              includeFallback: true
            })
          : Promise.resolve(null),
        fetchHostedModels(profile)
      ])

      const modelMap = new Map() // Use a Map to track unique models

      // Helper function to add model if not already present
      const addUniqueModel = (model: any) => {
        const key = model.modelId || model.hostedId
        if (!modelMap.has(key)) {
          modelMap.set(key, model)
        }
      }

      // Add hosted models
      hostedModelRes?.hostedModels.forEach(addUniqueModel)

      const desktopRuntimeModels = getDesktopRuntimeModels(desktopRuntimeConfig)
      desktopRuntimeModels.forEach(addUniqueModel)

      if (hostedModelRes) {
        setEnvKeyMap(hostedModelRes.envKeyMap)
        setAvailableHostedModels(
          Array.from(
            new Map(
              [...hostedModelRes.hostedModels, ...desktopRuntimeModels].map((model) => [
                model.modelId,
                model
              ])
            ).values()
          )
        )
      } else if (desktopRuntimeModels.length > 0) {
        setAvailableHostedModels(desktopRuntimeModels)
      }

      // Add OpenRouter models if available
      if (profile && (profile['openrouter_api_key'] || hostedModelRes?.envKeyMap['openrouter'])) {
        const openRouterModels = await fetchOpenRouterModels()
        if (openRouterModels) {
          openRouterModels.forEach(addUniqueModel)
          setAvailableOpenRouterModels(openRouterModels)
        }
      }

      // Add Ollama models if available
      if (process.env.NEXT_PUBLIC_OLLAMA_URL) {
        const localModels = await fetchOllamaModels()
        if (localModels) {
          localModels.forEach(addUniqueModel)
          setAvailableLocalModels(localModels)
        }
      }

      // Add custom models from state
      if (models && models.length > 0) {
        models.forEach((model) => {
          addUniqueModel({
            modelId: model.model_id as LLMID,
            modelName: model.name,
            provider: 'custom' as ModelProvider,
            hostedId: model.id,
            platformLink: '',
            imageInput: false,
            paid: 'paid' in model ? !!model.paid : false,
            maxContext: null
          })
        })
      }

      // Convert Map values back to array
      const nextModels = Array.from(modelMap.values())
      if (nextModels.length > 0) {
        setAllModels(nextModels)
        initRef.current.modelsLoaded = true
      }
    } catch (error) {
      console.error('Error loading models:', error)
    }
  }

  // Workspace images loading function (lowest priority)
  const loadWorkspaceImages = async (workspacesData: Tables<'workspaces'>[]) => {
    const images = await Promise.all(
      workspacesData.map(async (workspace): Promise<WorkspaceImage | null> => {
        const imagePath = workspace.image_path

        if (!imagePath) {
          return null
        }

        try {
          const workspaceImageUrl = await getWorkspaceImageFromStorage(imagePath)
          if (!workspaceImageUrl) {
            return null
          }

          const response = await fetch(workspaceImageUrl)
          const blob = await response.blob()
          const base64 = await convertBlobToBase64(blob)

          return {
            workspaceId: workspace.id,
            path: imagePath,
            base64,
            url: workspaceImageUrl
          }
        } catch (error) {
          console.error(`Error loading workspace image for ${workspace.id}:`, error)
          return null
        }
      })
    )

    const nextImages = images.filter((image): image is WorkspaceImage => image !== null)

    if (nextImages.length === 0) {
      return
    }

    setWorkspaceImages((prev) => {
      const existingWorkspaceIds = new Set(prev.map((image) => image.workspaceId))
      const missingImages = nextImages.filter(
        (image) => !existingWorkspaceIds.has(image.workspaceId)
      )

      if (missingImages.length === 0) {
        return prev
      }

      return [...prev, ...missingImages]
    })
  }

  const scheduleWorkspaceImagesLoad = (workspacesData: Tables<'workspaces'>[]) => {
    if (typeof window === 'undefined') {
      void loadWorkspaceImages(workspacesData)
      return
    }

    const load = () => {
      void loadWorkspaceImages(workspacesData).catch((error) => {
        console.error('Error loading workspace images:', error)
      })
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 8_000 })
      return
    }

    window.setTimeout(load, 3_000)
  }

  // Workspace data loading function (medium priority)
  const loadWorkspaceData = async (
    workspaceId: string,
    userId: string,
    isAuthenticated: boolean = true,
    selectionVersion?: number | null
  ) => {
    loadingWorkspaceRef.current = workspaceId
    try {
      const desktopWorkspace = isDesktopBrowserRuntime()
        ? await fetchDesktopWorkspaceContext(workspaceId)
        : null

      // Get full workspace data except for chats (which we've already loaded).
      // In desktop, prefer the local API route so auth/session locks in the browser
      // Supabase client cannot leave the context loader stuck forever.
      const workspaceResult = desktopWorkspace
        ? { data: desktopWorkspace, error: null }
        : await withTimeout(
            (supabase.from('workspaces') as unknown as WorkspaceQueryBuilder)
              .select(
                `*,
                folders(*),
                files(*),
                presets(*),
                prompts(*),
                tools(*),
                models(*)`
              )
              .eq('id', workspaceId)
              .single(),
            DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
            'Workspace context Supabase bootstrap'
          )

      const { data: workspace, error } = workspaceResult

      if (error) throw error

      if (!workspace) {
        console.error('No workspace found with ID:', workspaceId)
        return
      }

      if (!isWorkspaceSelectionCurrent(workspaceId, selectionVersion)) {
        return
      }

      const {
        files: nextFiles,
        folders: nextFolders,
        models: nextModels,
        presets: _nextPresets,
        prompts: nextPrompts,
        tools: nextWorkspaceTools,
        ...workspaceRecord
      } = workspace

      setWorkspaces((currentWorkspaces) =>
        currentWorkspaces.map((currentWorkspace) =>
          currentWorkspace.id === workspaceRecord.id
            ? {
                ...currentWorkspace,
                ...workspaceRecord
              }
            : currentWorkspace
        )
      )

      setSelectedWorkspaceState((currentWorkspace) =>
        currentWorkspace?.id === workspaceRecord.id
          ? {
              ...currentWorkspace,
              ...workspaceRecord
            }
          : currentWorkspace
      )

      const workspaceToolData = nextWorkspaceTools || []

      // Apply the core workspace context before loading optional catalogs.
      // Desktop catalog calls can be slow or unavailable; folders/files/prompts
      // should still hydrate as soon as the workspace context returns.
      if (workspaceToolData.length > 0) {
        setTools((currentTools) =>
          mergeToolCatalogs(
            ...currentTools.filter((tool) => tool.sharing === 'platform'),
            ...workspaceToolData
          )
        )
      }
      setFolders(nextFolders || [])
      setFiles(nextFiles || [])
      setPrompts(nextPrompts || [])
      setModels(nextModels || [])
      initRef.current.workspaceDataLoaded = true
      loadedWorkspaceRef.current = workspaceId

      // Load assistant, tool, and platform data independently so one failed
      // or hung optional catalog does not leave the entire workspace context empty.
      const [
        popularAssistantsResult,
        publicToolDataResult,
        customToolDataResult,
        platformToolDataResult,
        desktopRuntimeConfigResult
      ] = await Promise.allSettled([
        withTimeout(
          getPopularAssistants(workspaceId, userId),
          DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
          'Popular assistants bootstrap'
        ),
        withTimeout(getPublicTools(), DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS, 'Public tools bootstrap'),
        withTimeout(
          getCustomIntegrationToolsByUserId(userId),
          DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
          'Custom integrations bootstrap'
        ),
        withTimeout(
          getPlatformTools({ isAuthenticated }),
          DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
          'Platform tools bootstrap'
        ),
        isDesktopBrowserRuntime()
          ? withTimeout(
              getDesktopRuntimeConfig({
                backgroundRefresh: true,
                includeFallback: true
              }),
              DESKTOP_BOOTSTRAP_FETCH_TIMEOUT_MS,
              'Desktop runtime config bootstrap'
            )
          : Promise.resolve(null)
      ])

      if (!isWorkspaceSelectionCurrent(workspaceId, selectionVersion)) {
        return
      }

      const popularAssistants =
        popularAssistantsResult.status === 'fulfilled' ? popularAssistantsResult.value : []
      const publicToolData =
        publicToolDataResult.status === 'fulfilled' ? publicToolDataResult.value : []
      const customToolData =
        customToolDataResult.status === 'fulfilled' ? customToolDataResult.value : []
      const platformToolData =
        platformToolDataResult.status === 'fulfilled' ? platformToolDataResult.value : []
      const desktopRuntimeConfig =
        desktopRuntimeConfigResult.status === 'fulfilled' ? desktopRuntimeConfigResult.value : null
      const desktopRuntimeTools = getDesktopRuntimeTools(desktopRuntimeConfig)

      if (popularAssistantsResult.status === 'rejected') {
        safeConsole.warn('Failed to load popular assistants:', popularAssistantsResult.reason)
      }
      if (publicToolDataResult.status === 'rejected') {
        safeConsole.warn('Failed to load public tools:', publicToolDataResult.reason)
      }
      if (customToolDataResult.status === 'rejected') {
        safeConsole.warn('Failed to load custom integrations:', customToolDataResult.reason)
      }
      if (platformToolDataResult.status === 'rejected') {
        safeConsole.warn('Failed to load platform tools:', platformToolDataResult.reason)
      }
      if (desktopRuntimeConfigResult.status === 'rejected') {
        safeConsole.warn(
          'Failed to load desktop runtime config:',
          desktopRuntimeConfigResult.reason
        )
      }

      const nextPlatformTools = mergeToolCatalogs(...platformToolData, ...desktopRuntimeTools)

      // Update state with workspace data
      setTools(
        mergeToolCatalogs(
          ...nextPlatformTools,
          ...workspaceToolData,
          ...customToolData,
          ...publicToolData
        )
      )
      setAssistants(popularAssistants)
      setPlatformTools(nextPlatformTools)
      initRef.current.platformToolsLoaded = platformToolData.length > 0

      if (platformToolData.length === 0) {
        void recoverPlatformTools('workspace bootstrap returned no platform tools')
      }

      // Load assistant images in parallel
      loadAssistantImages(popularAssistants)

      // Update chat settings with workspace defaults
      setChatSettings((prevSettings) => ({
        model: (chatSettings?.model || workspaceRecord.default_model || 'losi-omega') as LLMID,
        prompt: workspaceRecord.default_prompt || 'You are a friendly, helpful AI assistant.',
        temperature: workspaceRecord.default_temperature || 0.5,
        contextLength: workspaceRecord.default_context_length || 4096,
        includeProfileContext: workspaceRecord.include_profile_context || true,
        includeWorkspaceInstructions: workspaceRecord.include_workspace_instructions || true,
        embeddingsProvider: (workspaceRecord.embeddings_provider as 'openai' | 'local') || 'openai',
        reflectMode: prevSettings.reflectMode || false
      }))
    } catch (error) {
      console.error('Error loading workspace data:', error)
      initRef.current.workspaceDataLoaded = false
    } finally {
      if (loadingWorkspaceRef.current === workspaceId) {
        loadingWorkspaceRef.current = null
      }
    }
  }

  useEffect(() => {
    if (platformTools.length === 0) {
      return
    }

    initRef.current.platformToolsLoaded = true
    setTools((currentTools) => {
      const mergedTools = mergeToolCatalogs(...platformTools, ...currentTools)

      return haveSameToolIds(mergedTools, currentTools) ? currentTools : mergedTools
    })
  }, [platformTools])

  useEffect(() => {
    if (!selectedWorkspace?.id || !profile?.user_id) {
      return
    }

    if (
      loadedWorkspaceRef.current === selectedWorkspace.id &&
      initRef.current.workspaceDataLoaded
    ) {
      return
    }

    if (loadingWorkspaceRef.current === selectedWorkspace.id) {
      return
    }

    // Reset workspace-scoped stores to avoid leaking previous workspace data.
    resetWorkspaceScopedState({ clearLoadedWorkspace: false })

    const workspaceId = selectedWorkspace.id
    const userId = profile.user_id as string

    void loadChatData(workspaceId, userId)
    void loadWorkspaceData(workspaceId, userId, true)
    prewarmWorkspacePulse(workspaceId)
  }, [prewarmWorkspacePulse, selectedWorkspace?.id, profile?.user_id])

  useEffect(() => {
    const profileUserId = profile?.user_id as string | null | undefined
    const workspaceId = selectedWorkspace?.id
    const scopeKey = getSpaceContextScopeKey(profileUserId, workspaceId)

    if (!scopeKey) {
      setHydratedSpaceContextScopeKey(null)
      return
    }

    if (hydratedSpaceContextScopeKey === scopeKey) {
      return
    }

    const restoredSpaceContext = readSelectedSpaceContextFromStorage({
      userId: profileUserId,
      workspaceId
    })

    setSelectedSpace(restoredSpaceContext?.selectedSpace ?? null)
    setSelectedSpaceItem(restoredSpaceContext?.selectedSpaceItem ?? null)
    setHydratedSpaceContextScopeKey(scopeKey)
  }, [
    profile?.user_id,
    selectedWorkspace?.id,
    hydratedSpaceContextScopeKey,
    getSpaceContextScopeKey
  ])

  useEffect(() => {
    const profileUserId = profile?.user_id as string | null | undefined
    const workspaceId = selectedWorkspace?.id
    const scopeKey = getSpaceContextScopeKey(profileUserId, workspaceId)

    if (!scopeKey || hydratedSpaceContextScopeKey !== scopeKey) {
      return
    }

    persistSelectedSpaceContextToStorage(
      {
        userId: profileUserId,
        workspaceId
      },
      {
        selectedSpace,
        selectedSpaceItem
      }
    )
  }, [
    profile?.user_id,
    selectedWorkspace?.id,
    selectedSpace,
    selectedSpaceItem,
    hydratedSpaceContextScopeKey,
    getSpaceContextScopeKey
  ])

  // Assistant images loading function (lowest priority)
  const loadAssistantImages = async (assistants: Tables<'assistants'>[]) => {
    const promises = assistants.map(async (assistant: any) => {
      try {
        let url = assistant.image_path ? getAssistantPublicImageUrl(assistant.image_path) : ''
        setAssistantImages((prev) => [
          ...prev,
          {
            assistantId: assistant.id,
            path: assistant.image_path,
            base64: '',
            url
          }
        ])
      } catch (error) {
        console.error(`Error loading assistant image for ${assistant.id}:`, error)
      }
    })

    Promise.all(promises).catch((error) => {
      console.error('Error loading assistant images:', error)
    })
  }

  // React to stable AuthProvider state. AuthProvider owns Supabase auth events
  // and recovery, so GlobalState should not register a second auth listener.
  useEffect(() => {
    const nextUserId = authSession?.user?.id || authUser?.id || null

    if (nextUserId) {
      if (
        authProviderSyncedUserIdRef.current === nextUserId ||
        currentSessionUserIdRef.current === nextUserId
      ) {
        authProviderSyncedUserIdRef.current = nextUserId
        return
      }

      authProviderSyncedUserIdRef.current = nextUserId
      void hydrateSessionWorkspaceState(nextUserId).catch((error) => {
        console.error('Failed to hydrate workspace state after auth user change:', error)
      })
      return
    }

    if (!currentSessionUserIdRef.current) {
      authProviderSyncedUserIdRef.current = null
      return
    }

    const signedOutUserId = currentSessionUserIdRef.current
    authProviderSyncedUserIdRef.current = null
    currentSessionUserIdRef.current = null
    scheduledModelsLoadUserIdRef.current = null
    pulseWarmupWorkspaceIdsRef.current.clear()
    setHydratedSpaceContextScopeKey(null)
    setProfile(null)
    setWorkspaces([])
    selectWorkspace(null)
    setWorkspaceImages([])
    resetWorkspaceScopedState()
    clearWorkspaceClientStorage(signedOutUserId)

    if (shouldRedirectToLoginOnSignOut(pathnameRef.current)) {
      routerRef.current.replace('/login')
    }
  }, [authSession?.user?.id, authUser?.id, selectWorkspace])

  // Sync chat settings to localStorage
  useEffect(() => {
    if (chatSettings) {
      try {
        localStorage.setItem('chatSettings', JSON.stringify(chatSettings))
      } catch (error) {
        console.error('Error saving chat settings:', error)
      }
    }
  }, [chatSettings])

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      // Update state to show the app is online
      // You can set a state variable here to show online status in the UI
    }

    const handleOffline = () => {
      // Update state to show the app is offline
      // You can set a state variable here to show offline status in the UI
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const syncChatActivityRegistry = useCallback(() => {
    setChatActivityStatuses(readChatActivityRegistry())
  }, [])

  const markChatActivitySeen = useCallback((chatId: string, options?: { requestId?: string }) => {
    const nextRegistry = markChatActivitySeenInRegistry(chatId, options)
    setChatActivityStatuses(nextRegistry)
  }, [])

  useEffect(() => {
    syncChatActivityRegistry()

    const handleRegistryChange = () => {
      syncChatActivityRegistry()
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === CHAT_ACTIVITY_REGISTRY_STORAGE_KEY) {
        syncChatActivityRegistry()
      }
    }

    window.addEventListener(CHAT_ACTIVITY_REGISTRY_EVENT, handleRegistryChange)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(CHAT_ACTIVITY_REGISTRY_EVENT, handleRegistryChange)
      window.removeEventListener('storage', handleStorage)
    }
  }, [syncChatActivityRegistry])

  const syncSidecarRegistry = useCallback(() => {
    setSidecarRunStatuses(readSidecarRunRegistry())
  }, [])

  const markSidecarChatSeen = useCallback((chatId: string, options?: { runId?: string }) => {
    markSidecarChatSeenInRegistry(chatId, options)
    setSidecarRunStatuses(readSidecarRunRegistry())
  }, [])

  useEffect(() => {
    syncSidecarRegistry()

    const handleRegistryChange = () => {
      syncSidecarRegistry()
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SIDECAR_CHAT_REGISTRY_STORAGE_KEY) {
        syncSidecarRegistry()
      }
    }

    window.addEventListener(SIDECAR_REGISTRY_EVENT, handleRegistryChange)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(SIDECAR_REGISTRY_EVENT, handleRegistryChange)
      window.removeEventListener('storage', handleStorage)
    }
  }, [syncSidecarRegistry])

  useEffect(() => {
    const getSidecarEventChatId = (value: unknown) => {
      if (typeof value !== 'string') return null

      const chatId = value.trim()
      return chatId || null
    }

    const handleToolStart = (event: {
      data?: {
        toolName?: string
        toolId?: string
        chatId?: string | null
        request?: { objective?: string; parameters?: { objective?: string } }
      }
    }) => {
      const data = event.data
      const eventChatId = getSidecarEventChatId(data?.chatId)

      if (
        !data ||
        data.toolName !== 'Sidecar__delegate' ||
        typeof data.toolId !== 'string' ||
        typeof eventChatId !== 'string' ||
        !data.toolId.startsWith('sidecar-')
      ) {
        return
      }

      const objective =
        data.request?.objective?.trim() || data.request?.parameters?.objective?.trim() || undefined

      const nextStatus = createSidecarChatStatus(
        {
          chatId: eventChatId,
          runId: data.toolId,
          status: 'running',
          objective,
          message: 'Starting Sidecar…'
        },
        { unread: false }
      )

      setSidecarRunStatuses((previous) => {
        const nextRegistry = upsertSidecarChatStatus(previous, nextStatus)
        writeSidecarChatRegistry(nextRegistry)
        return nextRegistry
      })
    }

    const handleRun = (event: { data?: SidecarRunEventData }) => {
      const data = event.data
      const eventChatId = getSidecarEventChatId(data?.chatId)

      if (!data || typeof eventChatId !== 'string') return

      const incomingStatus = createSidecarChatStatus(
        {
          ...data,
          chatId: eventChatId
        },
        {
          unread: Boolean(
            (data.completedAt || data.status === 'completed' || data.status === 'error') &&
              activeChatId !== eventChatId
          )
        }
      )

      setSidecarRunStatuses((previous) => {
        const nextRegistry = upsertSidecarChatStatus(previous, incomingStatus)
        writeSidecarChatRegistry(nextRegistry)
        return nextRegistry
      })
    }

    const handleStreamAnnotation = (event: {
      data?: {
        annotation?: Record<string, unknown>
        chatId?: string | null
        timestamp?: number
      }
    }) => {
      const data = event.data
      const eventChatId = getSidecarEventChatId(data?.chatId)

      if (!data || typeof eventChatId !== 'string') return

      const seededRun = buildSeedRunFromAnnotation({
        annotation: data.annotation,
        chatId: eventChatId,
        createdAt: data.timestamp ?? Date.now()
      })

      if (!seededRun) return

      const incomingStatus = createSidecarChatStatus(seededRun, {
        unread: Boolean(
          (seededRun.completedAt ||
            seededRun.status === 'completed' ||
            seededRun.status === 'error') &&
            activeChatId !== seededRun.chatId
        )
      })

      setSidecarRunStatuses((previous) => {
        const nextRegistry = upsertSidecarChatStatus(previous, incomingStatus)
        writeSidecarChatRegistry(nextRegistry)
        return nextRegistry
      })
    }

    addEventListener('tool.start', handleToolStart)
    addEventListener('sidecar.run', handleRun)
    addEventListener('stream.annotation', handleStreamAnnotation)

    return () => {
      removeEventListener('tool.start', handleToolStart)
      removeEventListener('sidecar.run', handleRun)
      removeEventListener('stream.annotation', handleStreamAnnotation)
    }
  }, [addEventListener, removeEventListener, activeChatId])

  // Status verification useEffect - ensures critical data is loaded
  useEffect(() => {
    const isPrivateRoute =
      Boolean(pathname) &&
      (pathname.startsWith('/chat') ||
        pathname.startsWith('/spaces') ||
        pathname.startsWith('/nexus') ||
        pathname.startsWith('/dashboard') ||
        pathname.startsWith('/setup') ||
        pathname.startsWith('/studio') ||
        pathname.startsWith('/agent') ||
        pathname.startsWith('/a/'))

    if (isPrivateRoute && initRef.current.initialFetchAttempted) {
      const hasProfileState = Boolean(profile || initRef.current.profileLoaded)
      const hasWorkspaceListState = workspaces.length > 0 || initRef.current.workspacesLoaded
      const hasSelectedWorkspaceState = Boolean(selectedWorkspace || selectedWorkspaceIdRef.current)
      const essentialDataMissing =
        !hasProfileState || !hasWorkspaceListState || !hasSelectedWorkspaceState
      const chatDataMissing = pathname?.startsWith('/chat') && !initRef.current.chatsLoaded
      const now = Date.now()
      const recoveryCooldownMs = 1500
      const chatRecoveryCooldownMs = 5000

      // If essential app state disappears after mount, restart initialization
      // in-place instead of leaving the route on a permanent loader.
      if (essentialDataMissing && !loading && initRef.current.retryCount < 3) {
        if (now - initRef.current.lastRecoveryAttemptAt < recoveryCooldownMs) {
          return
        }

        safeConsole.warn('Essential data missing but loading complete, retrying initialization...')
        initRef.current.retryCount += 1
        initRef.current.lastRecoveryAttemptAt = now
        initRef.current.initialFetchAttempted = false
        initRef.current.initialized = false
        setLoading(true)
        setInitializationAttempt((current) => current + 1)
        return
      }

      // Missing chat preload data should not blank the entire app.
      if (
        chatDataMissing &&
        selectedWorkspace?.id &&
        profile?.user_id &&
        now - initRef.current.lastChatRecoveryAttemptAt >= chatRecoveryCooldownMs
      ) {
        initRef.current.lastChatRecoveryAttemptAt = now
        safeConsole.warn('Chat data missing after resume, reloading in background...')
        void loadChatData(selectedWorkspace.id, profile.user_id as string)
      }

      if (
        pathname?.startsWith('/chat') &&
        profile?.user_id &&
        platformTools.length === 0 &&
        !platformToolsRecoveryInFlightRef.current
      ) {
        void recoverPlatformTools('chat route mounted without platform tools')
      }
    }
  }, [
    loading,
    pathname,
    platformTools.length,
    profile,
    recoverPlatformTools,
    selectedWorkspace,
    workspaces
  ])

  const isAuthSurface =
    pathname === '/login' || pathname?.startsWith('/login/')

  // Public /try chat must not sit behind the bootstrap gate: first-time
  // visitors would otherwise stare at the opaque "Loading your workspace"
  // skeleton while profile/tools load, and the auto-sent homepage question
  // can't start (or stream) until children mount. The try page tolerates a
  // null profile/workspace and manages its own loading state; bootstrap
  // continues in the background and the context value updates when ready.
  if (loading && !isAuthSurface && pathname !== '/try') {
    // Paint a real above-the-fold shell (large stable LCP element) instead of
    // a bare spinner while profile/workspace bootstrap runs. Children still do
    // NOT mount until `loading` is false, so context-dependent code (which can
    // read a null profile) stays gated exactly as before. The chat shell shows
    // for chat/space routes; everything else gets a neutral shell.
    const shellVariant =
      pathname?.startsWith('/chat') ||
      pathname?.startsWith('/spaces') ||
      pathname?.startsWith('/items') ||
      pathname === '/try'
        ? 'chat'
        : 'plain'
    // Interactive shell: the composer is a real textarea (draft is
    // queued/restored when children mount) and >10s shows Retry.
    // Children still do NOT mount until `loading` is false.
    return (
      <WorkspaceLoadingShell
        key={initializationAttempt}
        variant={shellVariant}
        onRetry={handleBootstrapRetry}
      />
    )
  }

  return (
    <QuickActionsProvider>
      <ChatbotUIContext.Provider
        value={{
          isContextInitialized: true,
          // PROFILE STORE
          profile,
          setProfile,
          applications,
          setApplications,
          // ITEMS STORE
          assistants,
          setAssistants,
          collections,
          setCollections,
          chats,
          setChats,
          hasMoreChats,
          isLoadingMoreChats,
          loadMoreChats,
          files,
          setFiles,
          folders,
          setFolders,
          models,
          setModels,
          presets,
          setPresets,
          prompts,
          setPrompts,
          tools,
          setTools,
          platformTools,
          setPlatformTools,
          workspaces,
          setWorkspaces,

          // MODELS STORE
          envKeyMap,
          setEnvKeyMap,
          availableHostedModels,
          setAvailableHostedModels,
          availableLocalModels,
          setAvailableLocalModels,
          availableOpenRouterModels,
          setAvailableOpenRouterModels,

          // WORKSPACE STORE
          selectedWorkspace,
          setSelectedWorkspace,
          refreshAccessibleWorkspaces,
          workspaceImages,
          setWorkspaceImages,

          // PRESET STORE
          selectedPreset,
          setSelectedPreset,

          // ASSISTANT STORE
          selectedAssistant,
          setSelectedAssistant,
          assistantImages,
          setAssistantImages,
          openaiAssistants,
          setOpenaiAssistants,

          // PASSIVE CHAT STORE
          chatMessages,
          setChatMessages,
          chatSettings,
          setChatSettings,
          selectedChat,
          setSelectedChat,
          chatFileItems,
          setChatFileItems,

          // ACTIVE CHAT STORE
          isGenerating,
          setIsGenerating,
          firstTokenReceived,
          setFirstTokenReceived,
          abortController,
          setAbortController,

          // CHAT INPUT COMMAND STORE
          isMessageHistoryPickerOpen,
          setIsMessageHistoryPickerOpen,
          isPromptPickerOpen,
          setIsPromptPickerOpen,
          slashCommand,
          setSlashCommand,
          isFilePickerOpen,
          setIsFilePickerOpen,
          hashtagCommand,
          setHashtagCommand,
          isToolPickerOpen,
          setIsToolPickerOpen,
          toolCommand,
          setToolCommand,
          focusPrompt,
          setFocusPrompt,
          focusFile,
          setFocusFile,
          focusTool,
          setFocusTool,
          focusAssistant,
          setFocusAssistant,
          atCommand,
          setAtCommand,
          isAssistantPickerOpen,
          setIsAssistantPickerOpen,
          promptVariables,
          setPromptVariables,
          showPromptVariables,
          setShowPromptVariables,

          // ATTACHMENT STORE
          chatFiles,
          setChatFiles,
          chatImages,
          setChatImages,
          newMessageFiles,
          setNewMessageFiles,
          newMessageImages,
          setNewMessageImages,
          showFilesDisplay,
          setShowFilesDisplay,
          activeAttachmentKey,
          activeAttachmentOverrideId,
          setActiveAttachmentOverrideId,
          getAttachmentStoreKey,
          getScopedAttachmentBindings,
          migrateAttachmentState,

          // RETRIEVAL STORE
          useRetrieval,
          setUseRetrieval,
          sourceCount,
          setSourceCount,

          // TOOL STORE
          selectedTools,
          setSelectedTools,
          toolInUse,
          setToolInUse,

          // PAYWALL
          isPaywallOpen,
          setIsPaywallOpen,

          showSidebar,
          setShowSidebar,

          isCreatePromptOpen,
          setIsCreatePromptOpen,

          allModels,
          setAllModels,

          // PROFILE SETTINGS
          isProfileSettingsOpen,
          setIsProfileSettingsOpen,

          // THINK DEEPER
          isThinkDeeperEnabled,
          setIsThinkDeeperEnabled,

          // SPACE NOTES (Nexus notes page)
          spaceNotes,
          setSpaceNotes,

          // SPACES STORE
          spaces,
          setSpaces,
          isLoadingSpaces,
          setIsLoadingSpaces,
          selectedSpace,
          setSelectedSpace,
          spaceMembers,
          setSpaceMembers,
          selectedSpaceItem,
          setSelectedSpaceItem,
          chatActivityStatuses,
          setChatActivityStatuses,
          markChatActivitySeen,
          sidecarChatStatuses,
          setSidecarChatStatuses,
          markSidecarChatSeen
        }}
      >
        {children}
        <CreatePrompt isOpen={isCreatePromptOpen} onOpenChange={setIsCreatePromptOpen} />
      </ChatbotUIContext.Provider>
    </QuickActionsProvider>
  )
}
